import React, { useRef } from "react";
import { generatePath, useParams } from "react-router";
import {
    Box,
    Button,
    CircularProgress,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    FormGroup,
    Grid,
    Paper,
    Skeleton,
    Stack,
    Switch,
    SxProps,
    Theme,
    Tooltip,
    useTheme,
} from "@mui/material";
import PlayCircleFilledWhiteIcon from "@mui/icons-material/PlayCircleFilledWhite";
import {
    ColumnDescriptor,
    FilterModal,
    FilterModalState,
    formatNumber,
    MteColumnEventWithTable,
    MultiTableComparator,
} from "@inode/multi-table-explorer";

import { PageContainer } from "#pages/PageContainer";
import { Caption, H1, H2, H5, H6, P } from "#common/headings";
import { Routes } from "#/config";
import { useAppDispatch, useAppSelector } from "#store";
import {
    isFavoritedInterpretation,
    selectEnvServiceUrl,
    selectInterpretationsRawById,
    selectTableResult,
    selectTableResultRawById,
    selectUiHistoryLatestInterpretationsByDate,
    selectUiUserId,
} from "#selectors";
import {
    fetchInterpretationFromServer,
    fetchInterpretationTableFromServer,
} from "#store/interpretations";
import { Link } from "#common/Link";
import { ErrorBoundary } from "#common/ErrorBoundary";
import { isDone } from "#store/types";
import {
    generateInterpretationCellContextMenu,
    generateInterpretationColumnContextMenu,
    generateInterpretationTableContextMenu,
} from "#components/Model/mteHelpers";
import { generateUrl } from "#components/App/routing/generateUrl";
import { ActionType, IQueryTableResult } from "#services/pilotService/types";
import { Interpretation } from "@inode/pilot-client-typescript-fetch";
import { push } from "redux-first-history";
import { setInterpretationFav } from "#store/ui";
import * as Logging from "#store/logging";
import { useQuery } from "react-query";
import { tableService } from "#services/pilotService";
import { pilotService } from "#services";
import {
    Cancel,
    Download,
    Send,
    ThumbDown,
    ThumbUp,
} from "@mui/icons-material";
import ZoomOutMapIcon from "@mui/icons-material/ZoomOutMap";
import ZoomInMapIcon from "@mui/icons-material/ZoomInMap";
import { tokenize } from "#/util/stringUtils";
import { useSnackbar } from "notistack";
import {
    deriveValues,
    Options,
    TokenizerOptions,
    WordCloudResponsive,
} from "@inode/wordcloud";
import { isPresent } from "#types";
import { computeTableDoi } from "#/util/columnSorting";
import { emptyTableMetaDescriptor, TableMetaDescriptor } from "@inode/types2";
import { PureHighlighter } from "#common/PureHighlighter";
import { Language } from "prism-react-renderer";
import { CodeEditor } from "#common/CodeEditor";
import { format } from "sql-formatter";
import { SqlLanguage } from "sql-formatter/lib/src/sqlFormatter";

type Props = {
    table: IQueryTableResult;
    interpretationId: number;
};

const Table = ({ table, interpretationId }: Props) => {
    const dispatch = useAppDispatch();
    const datasource = useAppSelector(
        state => state.uiState.selectedDatasource.name
    );

    const columnOrder = React.useMemo(() => {
        if (table && table.meta && table.meta.columns) {
            return table.meta?.columns
                ?.map((name, index) => {
                    return {
                        id: name,
                        relevance: !!table.meta.relevancies
                            ? table.meta.relevancies[index]
                            : 1.0,
                    };
                })
                .sort((x, y) => x.relevance - y.relevance);
        } else {
            return [];
        }
    }, [table]);

    const [hiddenColumns, setHiddenColumns] = React.useState(
        columnOrder.slice(7).map(x => x.id)
    );

    const [filterModalState, setFilterModalState] =
        React.useState<FilterModalState>({
            open: false,
            columnName: "",
        });

    const cellContextMenu = generateInterpretationCellContextMenu(
        [
            {
                table,
                interpretationIdx: interpretationId,
            },
        ],
        dispatch,
        datasource
    );
    const columnContextMenu = [
        {
            label: "Filter by values",
            action: (evt: MteColumnEventWithTable) => {
                dispatch(
                    Logging.log("click/by-value-context-menu-option", {
                        sql: table.sourceQuery,
                        columns: [evt.column],
                    })
                );

                setFilterModalState({
                    open: true,
                    columnName: evt.column,
                    table: table,
                });
            },
        },
        ...generateInterpretationColumnContextMenu(
            [
                {
                    table,
                    interpretationIdx: interpretationId,
                },
            ],
            dispatch,
            datasource
        ),
    ];
    const tableContextMenu = generateInterpretationTableContextMenu(
        [
            {
                table,
                interpretationIdx: interpretationId,
            },
        ],
        dispatch,
        datasource
    );

    return (
        <ErrorBoundary>
            <FilterModal
                state={filterModalState}
                handleClose={() =>
                    setFilterModalState({ ...filterModalState, open: false })
                }
                handleFilter={value => {
                    if (!value) {
                        return;
                    }
                    // dispatch(triggerExploreByFilter({
                    //     column: filterModalState.columnName,
                    //     operator: "<=",
                    //     value: value.
                    // }))

                    console.log("@hatieke: Implement your crazy logic here :)");
                    console.group();
                    console.log(
                        "Remember: Value is dependent on column type and may be null, too:)"
                    );
                    console.log(value);
                    console.log(filterModalState.columnName);
                    console.log(filterModalState.table);
                    console.groupEnd();

                    setFilterModalState({ ...filterModalState, open: false });

                    dispatch(
                        push(
                            generateUrl(
                                generatePath(Routes.triggerAction.path),
                                {
                                    q: table.sourceQuery,
                                    op: ActionType.QL_FILTER,
                                    column: filterModalState.columnName,
                                    value: JSON.stringify(value),
                                    operator: "=",
                                }
                            )
                        )
                    );
                }}
            />
            <MultiTableComparator
                wrapInTableContainer
                tables={[table]}
                dataExpandable={false}
                dataInitiallyExpanded={true}
                dataRowsInitiallyVisible={20}
                hideColumnTitle={true}
                hideTableInformation={true}
                columnOrder={columnOrder.map(o => o.id)}
                collapsedCols={hiddenColumns}
                cellContextMenu={cellContextMenu}
                columnContextMenu={columnContextMenu}
                tableContextMenu={tableContextMenu}
                onCollapsedColumnHeaderClick={(cols: string[]) => {
                    setHiddenColumns(
                        hiddenColumns.filter(c => !cols.includes(c))
                    );
                }}
            />
        </ErrorBoundary>
    );
};

const InterpretationCaption = ({
    interpretation,
}: {
    interpretation: Interpretation;
}) => {
    const serviceUrl = useAppSelector(selectEnvServiceUrl);
    const userId = useAppSelector(selectUiUserId);
    // const query = useAppSelector(selectUiQuery);

    const t = useQuery(["table", interpretation.table], () => {
        return tableService(serviceUrl, userId).getTableData(
            interpretation.table
        );
    });

    switch (t.status) {
        case "error":
            return <P>Oh, an error. Sorry.</P>;
        case "success":
            return <H1>{String(t.data.metadata?.caption || "n/a")}</H1>;
        case "idle":
            return <Skeleton variant={"text"} />;
        case "loading":
        default:
            return <CircularProgress />;
    }
};

const WrappedWordCloud = ({
    interpretation,
}: {
    interpretation: Interpretation;
}) => {
    const dispatch = useAppDispatch();
    const optionalTableIoState = useAppSelector(state =>
        selectTableResultRawById(state, interpretation.table)
    );
    React.useEffect(() => {
        console.log("fetch table for interpretation", interpretation.id);
        dispatch(fetchInterpretationTableFromServer(interpretation.id));
    }, [interpretation.id, dispatch]);

    const theme = useTheme();

    if (!isDone(optionalTableIoState)) {
        return <Skeleton variant={"rectangular"} />;
    } else {
        const table = optionalTableIoState.payload;

        if (!table.meta.descriptors) {
            return <P>No descriptors available</P>;
        }
        const headingsString = tokenize(
            table.columns.reduce((a, b) => a + " " + b, "")
        ).join(" ");
        const contentString = tokenize(
            table.data.flatMap(x => "" + x).reduce((a, b) => a + " " + b, "")
        ).join(" ");
        const valueOpts: TokenizerOptions = {
            allowNumbers: true,
            stemmer: "identity",
        };
        const wcOpts: Options = {
            rotations: 0,
            spiral: "rectangular",
            deterministic: true,
            enableTooltip: false,
            scale: "log",
            fontSizes: [10, 60],
            fontFamily: theme.typography.fontFamily,
            maxLayoutAttempts: 1,
        };
        const headingWords = deriveValues(headingsString, undefined, valueOpts);
        const contentWords = deriveValues(contentString, undefined, valueOpts);
        return (
            <Grid container sx={{ paddingTop: theme.spacing(2) }}>
                <Grid item xs={6}>
                    <H6>Headings</H6>
                    <P>Most mentioned words in all column headings.</P>
                    <WordCloudResponsive
                        words={headingWords}
                        options={wcOpts}
                        maxHeight={"20vh"}
                    />
                </Grid>
                <Grid item xs={6}>
                    <H6>Content</H6>
                    <P>Summarizes the content of all cells</P>
                    <WordCloudResponsive
                        words={contentWords}
                        options={wcOpts}
                        maxHeight={"20vh"}
                    />
                </Grid>
            </Grid>
        );
    }
};

const TableComponentIo: React.FC<{ interpretation: Interpretation }> = ({
    interpretation,
}) => {
    const ioTable = useAppSelector(state =>
        selectTableResultRawById(state, Number(interpretation.table))
    );

    if (!isDone(ioTable)) {
        return <Skeleton variant={"rectangular"} />;
    }
    const table = ioTable.payload;

    if (!table) {
        return (
            <Container>
                <P>table is undefined</P>
            </Container>
        );
    } else {
        return (
            <React.Fragment>
                <ErrorBoundary>
                    <Table table={table} interpretationId={interpretation.id} />
                </ErrorBoundary>
            </React.Fragment>
        );
    }
};

function FavoritedInterpretationSwitch(i: Interpretation) {
    const dispatch = useAppDispatch();
    const isHightlight = useAppSelector(state =>
        isFavoritedInterpretation(state, i.id)
    );
    const [checked, setChecked] = React.useState(isHightlight);
    React.useEffect(() => {
        setChecked(isHightlight);
    }, [isHightlight]);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        dispatch(setInterpretationFav(i.id, event.target.checked));
        setChecked(event.target.checked);
    };

    return (
        <FormGroup>
            <FormControlLabel
                control={<Switch checked={checked} onChange={handleChange} />}
                label={"Add interpretation to data library"}
            />
        </FormGroup>
    );
}

export type ColumnDescriptorExtendedWithScore = {
    score: number;
    name: string;
    index: number;
    descriptor: TableMetaDescriptor;
};

const ColumnChips = ({
    interpretation,
    limit = 999,
}: {
    interpretation: Interpretation;
    limit?: number;
}) => {
    const dispatch = useAppDispatch();
    const table = useAppSelector(state =>
        selectTableResult(state, interpretation.table)
    );

    const doiWeights = useAppSelector(state => {
        return state.uiState.doiWeights;
    });
    const favoriteColumns = useAppSelector(state => {
        return state.uiState.favoriteColumns;
    });
    const userHighlight: Interpretation[] = useAppSelector(
        selectUiHistoryLatestInterpretationsByDate
    );

    React.useEffect(() => {
        console.log("fetch table for interpretation", interpretation.id);
        dispatch(fetchInterpretationTableFromServer(interpretation.id));
    }, [interpretation.id, dispatch]);

    const cols: ColumnDescriptorExtendedWithScore[] = React.useMemo(() => {
        if (table?.meta.columns) {
            const doiScores = computeTableDoi(
                table,
                doiWeights,
                undefined,
                undefined,
                favoriteColumns
            );
            return table.meta.columns
                .map((colName, index) => {
                    return {
                        name: colName || "",
                        score: doiScores[index],
                        index: index,
                        descriptor: table?.meta.descriptors
                            ? table?.meta.descriptors[index]
                            : emptyTableMetaDescriptor,
                    };
                })
                .sort((a, b) => -a.score + b.score)
                .slice(0, Math.min(limit, table.meta.columns.length));
        } else {
            return [];
        }
    }, [
        table,
        table?.meta.columns,
        table?.meta.descriptors,
        doiWeights,
        favoriteColumns,
        userHighlight,
        table?.meta.columns,
        table?.meta.descriptors,
        limit,
    ]);

    if (!isPresent(table)) {
        return <Skeleton variant={"rectangular"} />;
    }
    return (
        <React.Fragment>
            <ErrorBoundary>
                <H5>Column details</H5>
                <P>
                    The result contains {cols.length} columns, with an average
                    relevance of{" "}
                    {formatNumber(
                        cols.map(x => x.score).reduce((x, y) => x + y, 0) /
                            cols.length
                    )}
                    .
                </P>
                <br />
                <Grid container spacing={2}>
                    {cols.map(c => {
                        return (
                            <Grid
                                item
                                xs={12}
                                md={4}
                                lg={3}
                                xl={2}
                                key={c.index}
                            >
                                <ColumnDescriptor
                                    descriptor={c.descriptor}
                                    label={c.name}
                                />
                                <Caption>
                                    Relevance: {formatNumber(c.score)}
                                </Caption>
                            </Grid>
                        );
                    })}
                </Grid>
            </ErrorBoundary>
        </React.Fragment>
    );
};

export const InterpretationPage = () => {
    const { id } = useParams();
    const dispatch = useAppDispatch();
    const { enqueueSnackbar } = useSnackbar();
    React.useEffect(() => {
        dispatch(fetchInterpretationFromServer(Number(id)));
    }, [id]);

    const ioInterpretation = useAppSelector(state =>
        selectInterpretationsRawById(state, Number(id))
    );

    // const [triggerActionModalActive, setTriggerActionModalActive] =
    //     React.useState(false);
    const datasourceDto = useAppSelector(
        state => state.uiState.selectedDatasource
    );

    const serviceUrl = useAppSelector(state => state.environment.SERVICE_URL);
    const rid = useAppSelector(state => state.uiState.rid);
    const csvForm = useRef<HTMLFormElement>(null);
    const csvFormQuery = useRef<HTMLTextAreaElement>(null);
    const csvFormDatabase = useRef<HTMLInputElement>(null);
    const csvFormRID = useRef<HTMLInputElement>(null);
    const csvFormSubmit = useRef<HTMLButtonElement>(null);

    const service = pilotService.actionService(serviceUrl, rid);

    const ioTable = useAppSelector(state =>
        selectTableResultRawById(
            state,
            Number(ioInterpretation?.payload?.table)
        )
    );

    const [upvoteFlag, setUpvoteFlag] = React.useState(
        ioTable?.payload?.meta["upvoted"] ? true : false
    );
    const [downvoteFlag, setDownvoteFlag] = React.useState(
        ioTable?.payload?.meta["downvoted"] ? true : false
    );

    const upVote = (
        table: IQueryTableResult,
        interpretation: Interpretation
    ) => {
        if (table.meta["upvoted"]) {
            return;
        }
        const nlQuery = table.meta["nlQuery"];
        if (nlQuery) {
            service.writeToLog("upvote", {
                sql: interpretation.query,
                nl: nlQuery,
                datasource: interpretation.dataset,
                operator:
                    interpretation.operatorBaseType +
                    "/" +
                    interpretation.operatorSpecificType,
            });
        }
        table.meta["upvoted"] = true;
        setUpvoteFlag(true);
    };

    const downVote = (
        table: IQueryTableResult,
        interpretation: Interpretation
    ) => {
        if (table.meta["downvoted"]) {
            return;
        }
        const nlQuery = table.meta["nlQuery"];
        if (nlQuery) {
            service.writeToLog("downvote", {
                sql: interpretation.query,
                nl: nlQuery,
                datasource: interpretation.dataset,
                operator:
                    interpretation.operatorBaseType +
                    "/" +
                    interpretation.operatorSpecificType,
            });
        }
        table.meta["downvoted"] = true;
        setDownvoteFlag(true);
    };

    // const onSave = React.useCallback((content: string) => {
    //     enqueueSnackbar(
    //         "Transmitting suggestion. Thank you for your feedback. The content has been reset to its original form.",
    //         {
    //             anchorOrigin: {
    //                 horizontal: "center",
    //                 vertical: "bottom",
    //             },
    //         }
    //     );
    //     setTriggerActionModalActive(true);
    //     setCurrentQuery(format(content));
    // }, []);

    if (!isDone(ioInterpretation) || !isDone(ioTable)) {
        return (
            <PageContainer>
                <H1>
                    Interpretation <Skeleton variant={"text"} />
                </H1>
            </PageContainer>
        );
    } else {
        const interpretation = ioInterpretation.payload;
        const table = ioTable.payload;
        const downloadCsv = (sql: string, dataset: string, rid: string) => {
            csvFormQuery.current!.value = sql;
            csvFormDatabase.current!.value = dataset;
            csvFormRID.current!.value = rid;
            csvForm.current!.requestSubmit(csvFormSubmit.current);
        };
        return (
            <PageContainer maxWidth={false}>
                <P>
                    Interpretation {interpretation.id}, produced by{" "}
                    <Link
                        to={generatePath(Routes.operatorInvocation.path, {
                            id: String(interpretation.operatorInvocation),
                        })}
                    >
                        operator {interpretation.operatorInvocation}
                    </Link>{" "}
                    of type{" "}
                    <code>
                        {interpretation.operatorBaseType}/
                        {interpretation.operatorSpecificType}
                    </code>{" "}
                    on dataset {interpretation.dataset}
                </P>
                <InterpretationCaption interpretation={interpretation} />

                <Grid
                    container
                    spacing={2}
                    sx={{ marginTop: theme => theme.spacing(1) }}
                >
                    <Grid item xs={10} container>
                        <Stack direction={"column"} width={"100%"}>
                            <H2>Table View</H2>
                            <TableComponentIo interpretation={interpretation} />
                            <H2>Structured query statement</H2>
                            <P>
                                As it has been produced by interpretation engine{" "}
                                {interpretation.operatorBaseType}/
                                {interpretation.operatorSpecificType}
                            </P>
                            <Stack direction={"row"} width={"100%"}>
                                <Box sx={{ flexGrow: 1 }}>
                                    <Paper
                                        sx={{
                                            padding: theme => theme.spacing(1),
                                            position: "relative",
                                        }}
                                    >
                                        <PureHighlighter
                                            code={interpretation.query}
                                            languageMode={
                                                datasourceDto.queryLanguage as Language
                                            }
                                        />
                                        <CodeEditorDialog
                                            code={interpretation.query}
                                            id={String(id)}
                                            languageMode={
                                                datasourceDto.queryLanguage as Language
                                            }
                                            sx={{
                                                position: "absolute",
                                                bottom: "0",
                                                right: "0",
                                            }}
                                        />

                                        {/*<Button*/}
                                        {/*    variant="outlined"*/}
                                        {/*    onClick={() =>*/}
                                        {/*        setTriggerActionModalActive(*/}
                                        {/*            true*/}
                                        {/*        )*/}
                                        {/*    }*/}
                                        {/*    sx={{*/}
                                        {/*        position: "absolute",*/}
                                        {/*        bottom: "0",*/}
                                        {/*        right: "0",*/}
                                        {/*    }}*/}
                                        {/*>*/}
                                        {/*    Edit*/}
                                        {/*</Button>*/}
                                    </Paper>
                                </Box>
                            </Stack>
                            <Grid container>
                                <WrappedWordCloud
                                    interpretation={interpretation}
                                />
                            </Grid>
                            <Grid item>
                                <ColumnChips interpretation={interpretation} />
                            </Grid>
                        </Stack>
                    </Grid>
                    <Grid item xs={2}>
                        <Box
                            sx={{
                                position: "sticky",
                                top: "5rem",
                                p: theme => theme.spacing(1),
                            }}
                        >
                            <Stack direction={"column"} spacing={1}>
                                <H5>Actions</H5>
                                <Tooltip
                                    title={
                                        "Upvote this interpretation. Title, SQL, and result (table) match your expectation"
                                    }
                                >
                                    <Button
                                        variant={"outlined"}
                                        onClick={() => {
                                            upVote(table, interpretation);
                                        }}
                                        startIcon={<ThumbUp />}
                                        color={
                                            upvoteFlag ? "success" : "primary"
                                        }
                                    >
                                        Vote up
                                    </Button>
                                </Tooltip>
                                <Tooltip
                                    title={
                                        "Downvote. Either title, SQL, or result are not as you expected."
                                    }
                                >
                                    <Button
                                        variant={"outlined"}
                                        onClick={() => {
                                            downVote(table, interpretation);
                                        }}
                                        startIcon={<ThumbDown />}
                                        color={
                                            downvoteFlag ? "error" : "primary"
                                        }
                                    >
                                        Vote down
                                    </Button>
                                </Tooltip>
                                <Tooltip
                                    title={
                                        "Explore additional recommended queries. This finds meaningful subsets in this data set."
                                    }
                                >
                                    <Button
                                        variant="outlined"
                                        onClick={() => {
                                            dispatch(
                                                push(
                                                    generateUrl(
                                                        generatePath(
                                                            Routes.triggerAction
                                                                .path
                                                        ),
                                                        {
                                                            q: interpretation.query,
                                                            op: ActionType.BY_RECOMMENDATION,
                                                            db: interpretation.dataset,
                                                        }
                                                    )
                                                )
                                            );
                                        }}
                                        startIcon={<ZoomInMapIcon />}
                                    >
                                        Recommended subsets
                                    </Button>
                                </Tooltip>
                                <Tooltip
                                    title={
                                        "Explore superset. Finds the smallest superset of this result."
                                    }
                                >
                                    <Button
                                        variant="outlined"
                                        startIcon={<ZoomOutMapIcon />}
                                        onClick={() => {
                                            dispatch(
                                                push(
                                                    generateUrl(
                                                        generatePath(
                                                            Routes.triggerAction
                                                                .path
                                                        ),
                                                        {
                                                            q: interpretation.query,
                                                            op: ActionType.QL_SUPERSET,
                                                            db: interpretation.dataset,
                                                        }
                                                    )
                                                )
                                            );
                                        }}
                                    >
                                        Superset
                                    </Button>
                                </Tooltip>
                                <Tooltip
                                    title={
                                        "Download CSV copy of the data; Full data set"
                                    }
                                >
                                    <Button
                                        variant="outlined"
                                        onClick={() => {
                                            downloadCsv(
                                                interpretation.query,
                                                interpretation.dataset!,
                                                rid
                                            );
                                        }}
                                        startIcon={<Download />}
                                    >
                                        Download
                                    </Button>
                                </Tooltip>
                                <FavoritedInterpretationSwitch
                                    {...interpretation}
                                />
                            </Stack>
                            {/*<Grid container spacing={2}>*/}
                            {/*    <Grid item xs={12}>*/}
                            {/*    </Grid>*/}

                            {/*    <Grid*/}
                            {/*        item*/}
                            {/*        alignContent={"center"}*/}
                            {/*        xs={12}*/}
                            {/*        spacing={1}*/}
                            {/*    >*/}
                            {/*    </Grid>*/}
                            {/*    <Grid item xs={6}>*/}
                            {/*    </Grid>*/}
                            {/*    <Grid item xs={6}>*/}
                            {/*    </Grid>*/}

                            {/*    <Grid item xs={12}>*/}
                            {/*    </Grid>*/}
                            {/*    <Grid item>*/}
                            {/*    </Grid>*/}
                            {/*</Grid>*/}
                        </Box>
                    </Grid>
                </Grid>
                <div style={{ display: "none" }}>
                    <form
                        ref={csvForm}
                        action={serviceUrl + "/server/ql2csv"}
                        method="post"
                        target="interpretation_download_target"
                    >
                        <textarea ref={csvFormQuery} name="query"></textarea>
                        <input
                            ref={csvFormDatabase}
                            type="text"
                            name="database"
                        />
                        <input ref={csvFormRID} type="text" name="rid" />
                        <button ref={csvFormSubmit} type="submit">
                            Submit
                        </button>
                    </form>
                    <iframe name="interpretation_download_target"></iframe>
                </div>
            </PageContainer>
        );
    }
};
type CodeEditorDialogProps = {
    languageMode: Language;
    sx?: SxProps<Theme>;
    code: string;
    id: string;
};

const CodeEditorDialog = ({
    code,
    id,
    sx = {},
    languageMode,
}: CodeEditorDialogProps) => {
    const dispatch = useAppDispatch();
    const datasourceDto = useAppSelector(
        state => state.uiState.selectedDatasource
    );

    const ioInterpretation = useAppSelector(state =>
        selectInterpretationsRawById(state, Number(id))
    );
    const serviceUrl = useAppSelector(state => state.environment.SERVICE_URL);
    const rid = useAppSelector(state => state.uiState.rid);
    const ioTable = useAppSelector(state =>
        selectTableResultRawById(
            state,
            Number(ioInterpretation?.payload?.table)
        )
    );
    const service = pilotService.actionService(serviceUrl, rid);

    const [queryCode, setQueryCode] = React.useState(() => {
        if (languageMode === "sql") {
            return format(code, { language: "sql" });
        } else {
            return code;
        }
    });
    const { enqueueSnackbar } = useSnackbar();

    const [triggerActionModalActive, setTriggerActionModalActive] =
        React.useState(false);
    const theme = useTheme();
    return (
        <>
            <Button
                sx={sx}
                variant={"outlined"}
                onClick={() => {
                    setTriggerActionModalActive(true);
                }}
            >
                Edit
            </Button>
            <Dialog
                onClose={() => setTriggerActionModalActive(false)}
                open={triggerActionModalActive}
            >
                <DialogTitle>Suggest a better code dialog</DialogTitle>
                <DialogContent>
                    <CodeEditor
                        code={queryCode}
                        onChange={c => setQueryCode(c)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setTriggerActionModalActive(false)}
                        variant={"outlined"}
                    >
                        Close
                    </Button>

                    {/*</Button>*/}
                    <Button
                        onClick={() => {
                            const nlQuery = ioTable?.payload?.meta["nlQuery"];
                            if (nlQuery) {
                                service.writeToLog("edit", {
                                    originalSql:
                                        ioInterpretation?.payload?.query,
                                    editedSql: queryCode,
                                    datasource:
                                        ioInterpretation?.payload?.dataset,
                                    nl: nlQuery,
                                    operator:
                                        ioInterpretation?.payload
                                            ?.operatorBaseType +
                                        "/" +
                                        ioInterpretation?.payload
                                            ?.operatorSpecificType,
                                });
                            }
                            setTriggerActionModalActive(false);
                            dispatch(
                                push(
                                    generateUrl(
                                        generatePath(Routes.triggerAction.path),
                                        {
                                            q: queryCode,
                                            op: ActionType.QL2DATA,
                                            o: datasourceDto.queryLanguage,
                                            db: datasourceDto.name,
                                        }
                                    )
                                )
                            );
                        }}
                        startIcon={<Send />}
                        variant={"contained"}
                    >
                        Send and Execute
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};
